import { useState, useEffect, useRef, useCallback } from 'react'
import { BellIcon, CheckCheckIcon, ExternalLinkIcon, Loader2Icon, RefreshCwIcon, BanknoteIcon, TargetIcon, ShieldCheckIcon, Settings2Icon } from 'lucide-react'
import { Link, useNavigate } from '@tanstack/react-router'
import { getNotificationRoute } from '@/lib/notification-routes'
import { cn } from '@/lib/utils'
import {
  getNotificationsApi,
  markAllNotificationsReadApi,
  markNotificationReadApi,
  type PortalNotification,
} from '@/lib/api'

// ── Category inference ─────────────────────────────────────────────────────

function inferCategory(message: string, apiCategory: string | null): 'payment' | 'circle' | 'verification' | 'system' {
  if (apiCategory) return apiCategory as 'payment' | 'circle' | 'verification' | 'system'
  const lower = message.toLowerCase()
  if (lower.includes('payment') || lower.includes('paid') || lower.includes('cycle') || lower.includes('due') || lower.includes('overdue') || lower.includes('payout') || lower.includes('settled') || lower.includes('payer')) {
    return 'payment'
  }
  if (lower.includes('pot') || lower.includes('circle') || lower.includes('full') || lower.includes('funded') || lower.includes('participant') || lower.includes('joined') || lower.includes('request')) {
    return 'circle'
  }
  if (lower.includes('verification') || lower.includes('verified') || lower.includes('verify') || lower.includes('approve') || lower.includes('reject')) {
    return 'verification'
  }
  return 'system'
}

function categoryBadge(cat: 'payment' | 'circle' | 'verification' | 'system'): string {
  switch (cat) {
    case 'payment': return 'bg-blue-500/10 text-blue-600'
    case 'circle': return 'bg-purple-500/10 text-purple-600'
    case 'verification': return 'bg-amber-500/10 text-amber-600'
    case 'system': return 'bg-muted text-muted-foreground'
  }
}

function categoryIcon(cat: 'payment' | 'circle' | 'verification' | 'system', className = 'size-3') {
  switch (cat) {
    case 'payment': return <BanknoteIcon className={className} />
    case 'circle': return <TargetIcon className={className} />
    case 'verification': return <ShieldCheckIcon className={className} />
    case 'system': return <Settings2Icon className={className} />
  }
}

// ── Helpers ────────────────────────────────────────────────────────────────

function timeAgo(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffSec = Math.floor(diffMs / 1000)

  if (diffSec < 60) return 'just now'
  const diffMin = Math.floor(diffSec / 60)
  if (diffMin < 60) return `${diffMin}m ago`
  const diffHr = Math.floor(diffMin / 60)
  if (diffHr < 24) return `${diffHr}h ago`
  const diffDay = Math.floor(diffHr / 24)
  if (diffDay < 7) return `${diffDay}d ago`
  const diffWeek = Math.floor(diffDay / 7)
  return `${diffWeek}w ago`
}

function formatRelativeDate(dateStr: string): string {
  const now = Date.now()
  const then = new Date(dateStr).getTime()
  const diffMs = now - then
  const diffDay = Math.floor(diffMs / 86_400_000)

  if (diffDay === 0) return 'Today'
  if (diffDay === 1) return 'Yesterday'
  if (diffDay < 7) return `${diffDay} days ago`

  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  })
}

// ── Component ──────────────────────────────────────────────────────────────

export default function NotificationBell() {
  const [notifications, setNotifications] = useState<PortalNotification[]>([])
  const [loading, setLoading] = useState(true)
  const [open, setOpen] = useState(false)
  const [markingAll, setMarkingAll] = useState(false)
  const [refreshing, setRefreshing] = useState(false)
  const bellRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  // ── Fetch notifications ──────────────────────────────────────────────
  const fetchNotifications = useCallback(async (showSpinner = false) => {
    try {
      if (showSpinner) setRefreshing(true)
      const res = await getNotificationsApi({ page: 1, limit: 10 })
      setNotifications(res.data)
    } catch {
      // silently fail
    } finally {
      setLoading(false)
      setRefreshing(false)
    }
  }, [])

  useEffect(() => {
    fetchNotifications()
  }, [fetchNotifications])

  // ── Poll every 60s ───────────────────────────────────────────────────
  useEffect(() => {
    const interval = setInterval(fetchNotifications, 60_000)
    return () => clearInterval(interval)
  }, [fetchNotifications])

  // ── Close on click outside ───────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function handleClick(e: MouseEvent) {
      if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [open])

  // ── Close on Escape ──────────────────────────────────────────────────
  useEffect(() => {
    if (!open) return
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false)
    }
    document.addEventListener('keydown', handleKey)
    return () => document.removeEventListener('keydown', handleKey)
  }, [open])

  // ── Mark all as read ─────────────────────────────────────────────────
  const handleMarkAllRead = async () => {
    setMarkingAll(true)
    try {
      await markAllNotificationsReadApi()
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })))
    } catch {
      // silently fail
    } finally {
      setMarkingAll(false)
    }
  }

  // ── Mark single notification as read ─────────────────────────────────
  const handleMarkRead = async (id: number) => {
    // Optimistic update
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
    )
    try {
      await markNotificationReadApi(id)
    } catch {
      // Revert on failure
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: false } : n)),
      )
    }
  }

  // ── Derived ──────────────────────────────────────────────────────────
  const unreadCount = notifications.filter((n) => !n.is_read).length

  // ── Render ───────────────────────────────────────────────────────────
  return (
    <div ref={bellRef} className="relative">
      <button
        type="button"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="relative flex size-11 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
      >
        <BellIcon className="size-5" />
        {unreadCount > 0 && (
          <span className="absolute right-2 top-2 flex size-4 items-center justify-center rounded-full bg-red-500 text-[10px] font-bold leading-none text-white ring-2 ring-background">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div
          className={cn(
            'absolute right-0 top-full z-50 mt-2 w-80 rounded-xl border border-border bg-card shadow-xl',
            'motion-safe:animate-in motion-safe:fade-in motion-safe:zoom-in-95 motion-safe:duration-150',
          )}
        >
          {/* ── Header ── */}
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => fetchNotifications(true)}
                disabled={refreshing}
                className="flex size-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                aria-label="Refresh notifications"
                title="Refresh"
              >
                <RefreshCwIcon className={cn('size-3.5', refreshing && 'animate-spin')} />
              </button>
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllRead}
                  disabled={markingAll}
                  className="flex items-center gap-1.5 rounded-md px-1.5 py-1 text-xs font-medium text-muted-foreground transition-colors hover:bg-muted hover:text-foreground disabled:opacity-50"
                >
                  {markingAll ? (
                    <Loader2Icon className="size-3 animate-spin" />
                  ) : (
                    <CheckCheckIcon className="size-3.5" />
                  )}
                  Mark all read
                </button>
              )}
            </div>
          </div>

          {/* ── List ── */}
          <div className="max-h-80 overflow-y-auto">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2Icon className="size-5 animate-spin text-muted-foreground" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="flex flex-col items-center gap-2 px-4 py-8 text-center">
                <BellIcon className="size-8 text-muted-foreground/30" />
                <p className="text-sm text-muted-foreground">No notifications yet</p>
                <Link
                  to="/dashboard/notification-preferences"
                  onClick={() => setOpen(false)}
                  className="text-xs font-medium text-[var(--brand-mint)] transition-colors hover:underline"
                >
                  Manage notification settings
                </Link>
              </div>
            ) : (
              <div className="divide-y divide-border/50">
                {/* ── Date headers ── */}
                {notifications
                  .reduce<
                    Array<{
                      date: string
                      items: PortalNotification[]
                    }>
                  >((groups, n) => {
                    const dateLabel = formatRelativeDate(n.scheduled_at || n.created_at)
                    const existing = groups.find((g) => g.date === dateLabel)
                    if (existing) {
                      existing.items.push(n)
                    } else {
                      groups.push({ date: dateLabel, items: [n] })
                    }
                    return groups
                  }, [])
                  .map((group) => (
                    <div key={group.date}>
                      <p className="sticky top-0 bg-card px-4 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/50">
                        {group.date}
                      </p>
                      {group.items.map((n) => (
                        <button
                          key={n.id}
                          type="button"
                          onClick={() => {
                            if (!n.is_read) handleMarkRead(n.id)
                            const cat = inferCategory(n.message, n.notification_category)
                            const route = getNotificationRoute(cat)
                            if (route) { setOpen(false); navigate({ to: route }) }
                          }}
                          className={cn(
                            'flex w-full items-start gap-3 px-4 py-3 text-left transition-colors hover:bg-muted/50',
                            !n.is_read && 'bg-[var(--brand-mint)]/[0.04]',
                          )}
                        >
                          <span
                            className={cn(
                              'mt-[5px] size-2 shrink-0 rounded-full',
                              n.is_read ? 'bg-transparent' : 'bg-[var(--brand-mint)]',
                            )}
                          />
                          <div className="min-w-0 flex-1">
                            <p
                              className={cn(
                                'text-sm leading-snug',
                                n.is_read
                                  ? 'text-muted-foreground'
                                  : 'font-medium text-foreground',
                              )}
                            >
                              {n.message}
                            </p>
                            <div className="mt-1 flex items-center gap-1.5">
                              <span className={cn(
                                'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[9px] font-medium',
                                categoryBadge(inferCategory(n.message, n.notification_category)),
                              )}>
                                {categoryIcon(inferCategory(n.message, n.notification_category), 'size-2.5')}
                                {inferCategory(n.message, n.notification_category)}
                              </span>
                              <span className="text-[11px] text-muted-foreground/60">
                                {timeAgo(n.scheduled_at || n.created_at)}
                              </span>
                            </div>
                          </div>
                          {!n.is_read && (
                            <ExternalLinkIcon className="mt-1 size-3 shrink-0 text-[var(--brand-mint)]" />
                          )}
                        </button>
                      ))}
                    </div>
                  ))}
              </div>
            )}
          </div>

          {/* ── Footer ── */}
          <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
            <p className="text-[11px] text-muted-foreground/50">
              Auto-refresh every 60s
            </p>
            <Link
              to="/dashboard/notifications"
              onClick={() => setOpen(false)}
              className="flex items-center gap-1 text-[11px] font-medium text-[var(--brand-mint)] transition-colors hover:underline"
            >
              View all
              <ExternalLinkIcon className="size-3" />
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
